import * as React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import CheckIcon from "@mui/icons-material/Check";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RedeemIcon from "@mui/icons-material/Redeem";
import TaskIcon from "@mui/icons-material/Task";
import CancelIcon from "@mui/icons-material/Cancel";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";

import {
  PLACED,
  ACCEPTED,
  REJECTED,
  FULFILLED,
  DELIVERED,
  ORDER_STATE_ACCEPTED,
  ORDER_STATE_FULFILLED,
  ORDER_STATE_DELIVERED,
  ORDER_STATE_CANCELLED,
  ORDER_STATE_AGENT_ASSIGNED,
  ORDER_STATE_PICKED_UP,
  ORDER_STATE_OUT_FOR_DELIVERY,
  ORDER_STATE_RTO_INITIATED,
  ORDER_STATE_RTO_DELIVERED,
  AGENT_ASSIGNED,
  OUT_FOR_DELIVERY,
  PICKED_UP,
} from "../../../constants/string";

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient( 95deg, rgb(0,107,225) 25%, rgb(0, 45, 121) 100%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient( 95deg, rgb(0,107,225) 25%, rgb(0, 45, 121) 100%)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    backgroundImage: "linear-gradient( 136deg, rgb(0,107,225) 25%, rgb(0, 45, 121) 100%)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    backgroundImage: "linear-gradient( 136deg, rgb(0,107,225) 25%, rgb(0, 45, 121) 100%)",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <ShoppingCartCheckoutOutlinedIcon />,
    2: <TaskIcon />,
    3: <RedeemIcon />,
    4: <AssignmentIndOutlinedIcon />,
    5: <PlaylistAddCheckOutlinedIcon />,
    6: <LocalShippingIcon />,
    7: <CheckIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

function ColorlibStepIconFailedOrder(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <TaskIcon />,
    2: <CancelIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

const Steppers = ({ orderStatus, selfDelivery }) => {
  const steps = selfDelivery
    ? [PLACED, ACCEPTED, FULFILLED, AGENT_ASSIGNED, PICKED_UP, OUT_FOR_DELIVERY, DELIVERED]
    : [PLACED, ACCEPTED, FULFILLED];
  const cancelledOrderSteps = [PLACED, REJECTED];
  let activeStep = 0;

  switch (orderStatus) {
    case ORDER_STATE_ACCEPTED:
      activeStep = 1;
      break;
    case ORDER_STATE_FULFILLED:
      activeStep = 2;
      break;
    case ORDER_STATE_AGENT_ASSIGNED:
      activeStep = 3;
      break;
    case ORDER_STATE_PICKED_UP:
      activeStep = 4;
      break;
    case ORDER_STATE_OUT_FOR_DELIVERY:
      activeStep = 5;
      break;
    case ORDER_STATE_DELIVERED:
      activeStep = 6;
      break;
    default:
      activeStep = 0;
  }

  const getVisibleSteps = () => {
    if (!selfDelivery) return steps;

    const start = Math.max(0, activeStep - 2);
    const end = Math.min(activeStep + 2, steps.length);
    return steps.slice(start, end);
  };

  const visibleSteps = getVisibleSteps();
  const stepIndexOffset = Math.max(0, activeStep - 2); 
  const adjustedActiveStep = orderStatus === ORDER_STATE_DELIVERED ? 3 : activeStep - stepIndexOffset; 

  return (
    <Stack sx={{ marginTop: 2, flex: 1 }}>
      {orderStatus === ORDER_STATE_CANCELLED ||
      orderStatus === ORDER_STATE_RTO_INITIATED ||
      orderStatus === ORDER_STATE_RTO_DELIVERED ? (
        <Stepper alternativeLabel activeStep={1} connector={<ColorlibConnector />}>
          {cancelledOrderSteps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={ColorlibStepIconFailedOrder}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      ) : (
        <Stepper
          alternativeLabel
          activeStep={adjustedActiveStep}
          connector={<ColorlibConnector />}
        >
          {visibleSteps.map((label, index) => (
            <Step key={label}>
              <StepLabel StepIconComponent={ColorlibStepIcon} icon={stepIndexOffset + index + 1}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
    </Stack>
  );
};

export default Steppers;
